import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useStore } from './store'
import { useScroll, useIntersect, MeshDistortMaterial, useTexture } from "@react-three/drei"
import * as THREE from 'three'

export function useIsInViewport(ref) {
    const [isIntersecting, setIsIntersecting] = useState(false)
  
    const observer = useMemo(
      () =>
        new IntersectionObserver(([entry]) =>
          setIsIntersecting(entry.isIntersecting),
        ),
      [],
    )
  
    useEffect(() => {
      observer.observe(ref.current)
  
      return () => {
        observer.disconnect()
      }
    }, [ref, observer])
  
    return isIntersecting
}

export function HiddenText({show, delay, vertical, single, noload, ...props}) {
    const ref = useRef()
    const isInViewport = useIsInViewport(ref)
    const seen = useRef(false)
    const loaded = useStore(state => state.loaded)
    useEffect(() => {
      if((isInViewport || show) && !seen.current && (loaded || noload)) {
        seen.current = true
        
        setInterval(() => (ref?.current?.children ? [...ref?.current?.children] : []).forEach((el, i) => setTimeout(() => el.classList.add('hidden-text-visible'), i*30)),
         delay ? delay * 1000 : 0) 
      }
    }, [isInViewport, loaded])
    const content = single ? [props.children] : props.children.split('')
    return (
      <div {...props} ref={ref} className={`hidden-text-container ${props.className} ${vertical ? 'vertical' : ''}`}>
        {content.map((c, i) => 
              <span className="hidden-text-wrap" key={i}>
                <span className="hidden-text-content">{c}</span>
              </span>
          )}
      </div>
    )
}

export function CascadeText({hidden, invert, nofade, nohover, delay, ...props}) {
  const ref = useRef()
  const isInViewport = useIsInViewport(ref)
  const hover = useStore(state => state.hover)
  const unhover = useStore(state => state.unhover)
  const seen = useRef(false)
  const loaded = useStore(state => state.loaded)
  useEffect(() => {
    if(isInViewport && !seen.current && loaded) {
      seen.current = true
      
      setInterval(() => ref?.current?.classList.remove('hidden'), delay ? delay * 1000 : 0) 
    }
  }, [isInViewport, loaded])
  return (
    <div {...props} ref={ref} 
    onMouseEnter={() => {
      [...ref.current.children].forEach((el, i) => setTimeout(() => el.classList.add('hovered'), i*70))
      if(!nohover) {
        const pos = ref.current.getBoundingClientRect()
        hover((pos.x + pos.right)/2, (pos.y + pos.bottom)/2, Math.max(pos.bottom-pos.y, pos.right - pos.x) * 0.8)
      }
    }}
    onMouseLeave={() => {
      [...ref.current.children].forEach((el, i) => setTimeout(() => el.classList.remove('hovered'), i*70))
      unhover()
    }}
    className={`cascade-text-container ${props.className} ${invert ? 'inverted' : ''} ${nofade ? '' : 'fadeout-enabled'} ${hidden ? 'hidden' : ''}`}>
      {props.children.split('').map((c, i) => 
            <span className="cascade-text-wrap" key={i}>
              <div className="cascade-text-content">{c}</div>
              <div className="cascade-text-content">{c}</div>
            </span>
        )}
    </div>
  )
}

export function ParallaxDiv({ children, speed = 0, horizontal = false }) {
    const data = useScroll()
    const ref = useRef()
    let translateX = 0, translateY = 0
    if(!Array.isArray(speed)) speed = [speed, 0]
    useFrame((state, delta) => {
      translateY = -data.offset * speed[0] * 1000
      translateX = -data.offset * speed[1] * 1000
      ref.current.style.transform = `translate3d(${horizontal ? translateX : 0}px, ${translateY}px, 0)`
    })
    
    return (
      <div ref={ref}>
        {children}
      </div>
    )
}

export function Rig() {
  const data = useScroll()
  const transition = useRef(useStore.getState().transition)
  useEffect(() => useStore.subscribe(
    state => (transition.current = state.transition)
  ), [])
  let r = 4
  useFrame((state) => {
    if(data.offset <= 1/16)  {
      const time = state.clock.getElapsedTime() * 0.3
      state.camera.position.set(Math.cos(-time)*r, state.camera.position.y, Math.sin(-time)*r)
      state.camera.lookAt(0, 0, 0)
    }
  })
}

export function DistortImage({ url, scale, show, idle = true, ...props }) {
  const texture = useTexture(url)
  const visible = useRef(false)
  const ref = useIntersect((isVisible) => (visible.current = isVisible))
  let seen = false
  const data = useScroll()
  
  const transition = useRef(useStore.getState().transition)
  useEffect(() => useStore.subscribe(
    state => (transition.current = state.transition)
  ), [])

  const hovering = useRef(false)

  if(!Array.isArray(scale)) scale = [scale, scale, scale]
  useFrame((state, delta) => {
    if(visible.current) seen = true
    const distort = idle ? Math.min(data.delta*400, 0.2) + (transition.current ? 0.25 : 0) : hovering.current ? 0 : 0.2
    ref.current.material.distort = THREE.MathUtils.lerp(ref.current.material.distort, distort, 0.01)
    const width = THREE.MathUtils.lerp(ref.current.scale.x, (visible.current || seen || show) && !transition.current ? scale[0] : 0, 0.05)
    texture.repeat.set(width/scale[0], 1)
    ref.current.scale.set(width, scale[1], scale[2])
    ref.current.position.x = props.position[0] + (width/2 - scale[0]/2) * (transition.current ? -1 : 1)
    //ref.current.quaternion.copy(state.camera.quaternion)
  })
  return (
    <mesh ref={ref} {...props} scale={[0, scale[1], scale[2]]} onPointerEnter={() => hovering.current = true} onPointerLeave={() => hovering.current = false}>
      <planeBufferGeometry args={[1, 1, 32, 32]} />
      <MeshDistortMaterial map={texture} speed={idle ? 5 : 2}/>
    </mesh>
  )
}